/**
 * Bundle tiers — csomagajánlat (blocks/bundle-tiers.liquid).
 *
 * A kártya a termék űrlap mennyiség mezőjét állítja a csomag darabszámára. Az árat a
 * csomagkedvezmény Function számolja a kosárban és a Releasit utánvét űrlapon, ezért itt
 * nincs árlogika. Az aktív kártya mindig a mennyiség szerinti legnagyobb elért csomag,
 * ugyanazzal a szabállyal, amit a Function alkalmaz. Betöltéskor a data-default-qty
 * (a 2 db-os csomag) kerül az űrlap mennyiség mezőjébe.
 *
 * Asztalon a sorok és a sorközök annyival nyúlnak (--bt-fill), hogy a vásárlási blokk alja
 * egy vonalba kerüljön a bal oszlop (kép + jelvények) aljával.
 */
(() => {
  const MAX_FILL_PER_SLOT = 40;

  class BundleTiersComponent extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound !== 'true') {
        this.dataset.bound = 'true';

        this.addEventListener('change', (event) => {
          const radio = event.target.closest('.bundle-tiers__radio');
          if (radio) this.setQuantity(Number(radio.dataset.qty) || 1);
        });

        this.quantityInput()?.addEventListener('change', () => this.syncFromQuantity());

        // Az űrlap 1-gyel indul, ezért az alapértelmezett csomagot rá kell írni a mennyiségre —
        // e nélkül a kártya rögtön visszaugrana az 1 db-os sorra.
        this.setQuantity(Number(this.dataset.defaultQty) || 1);
      } else {
        this.syncFromQuantity();
      }

      this.fitToMediaColumn();
    }

    disconnectedCallback() {
      this.stopFitting?.();
      this.stopFitting = null;
    }

    quantityInput() {
      const form = document.getElementById(this.dataset.formId || '');
      return form ? form.querySelector('input[name="quantity"]') : null;
    }

    radios() {
      return Array.from(this.querySelectorAll('.bundle-tiers__radio'));
    }

    setQuantity(qty) {
      const input = this.quantityInput();
      if (!input) return;
      if (Number(input.value) === qty) {
        this.syncFromQuantity();
        return;
      }
      input.value = String(qty);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromQuantity() {
      const qty = Number(this.quantityInput()?.value) || 1;
      const radios = this.radios();
      const active = radios.filter((radio) => Number(radio.dataset.qty) <= qty).pop() || null;

      radios.forEach((radio) => {
        radio.checked = radio === active;
        radio.closest('.bundle-tiers__item')?.classList.toggle('is-active', radio === active);
      });
    }

    fitToMediaColumn() {
      const grid = this.closest('.product-information__grid');
      const details = this.closest('.product-details');
      const buttons = this.closest('.product-form-buttons');
      const left =
        grid?.querySelector(':scope > .trust-badges') || grid?.querySelector(':scope > .product-information__media');
      if (!grid || !details || !buttons || !left || this.stopFitting || !('ResizeObserver' in window)) return;

      const desktop = window.matchMedia('(min-width: 750px)');
      const slots = this.radios().length * 2 - 1; // sorok + sorközök
      let fill = 0;

      const apply = (next) => {
        fill = next;
        if (fill > 0) this.style.setProperty('--bt-fill', `${(fill / slots).toFixed(2)}px`);
        else this.style.removeProperty('--bt-fill');
      };

      const update = () => {
        if (!desktop.matches) {
          if (fill) apply(0);
          return;
        }
        // Mindkét oszlopot a saját tetejéhez mérjük, így a ragadós jobb oszlop görgetés közben sem torzít.
        const leftHeight = left.getBoundingClientRect().bottom - grid.getBoundingClientRect().top;
        const buyHeight = buttons.getBoundingClientRect().bottom - details.getBoundingClientRect().top;
        const next = Math.min(MAX_FILL_PER_SLOT * slots, Math.max(0, Math.round(fill + leftHeight - buyHeight)));
        if (Math.abs(next - fill) >= 2) apply(next);
      };

      let frame = 0;
      const schedule = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      };

      // A ResizeObserver csak méretváltozást jelez, elmozdulást nem: ezért figyeljük a rácsot, a galériát
      // és a részletek tartalmát is (kép, betűtípus, Releasit gomb betöltése), plusz a teljes betöltést.
      const observer = new ResizeObserver(schedule);
      [grid, left, grid.querySelector(':scope > .product-information__media'), details.firstElementChild, buttons]
        .filter(Boolean)
        .forEach((element) => observer.observe(element));
      desktop.addEventListener('change', schedule);
      window.addEventListener('load', schedule, { once: true });

      this.stopFitting = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        desktop.removeEventListener('change', schedule);
        window.removeEventListener('load', schedule);
      };
    }
  }

  if (!customElements.get('bundle-tiers-component')) {
    customElements.define('bundle-tiers-component', BundleTiersComponent);
  }
})();
